import type { Page } from './types';

export const PATTERN_LANGUAGES = ['en', 'ja', 'zh-TW'] as const;
export type PatternLanguage = (typeof PATTERN_LANGUAGES)[number];

export function isPatternLanguage(v: unknown): v is PatternLanguage {
    return typeof v === 'string' && (PATTERN_LANGUAGES as readonly string[]).includes(v);
}

/** Maps the UI language to a pattern language; anything unrecognised is `en`. */
export function patternLanguageFromUi(uiLang: string): PatternLanguage {
    if (isPatternLanguage(uiLang)) return uiLang;
    const base = uiLang.split('-')[0];
    if (base === 'ja') return 'ja';
    if (uiLang === 'zh-Hant' || uiLang === 'zh-HK' || uiLang.startsWith('zh-Hant-')) return 'zh-TW';
    return 'en';
}

const KANA = /[぀-ヿㇰ-ㇿ]/;
const HAN = /\p{Script=Han}/u;

type LexicalNodeLike = { type?: unknown; text?: unknown; children?: unknown };

function collectLexicalText(node: unknown, out: string[]): void {
    if (typeof node !== 'object' || node === null) return;
    const n = node as LexicalNodeLike;
    if (n.type === 'text' && typeof n.text === 'string') out.push(n.text);
    if (Array.isArray(n.children)) for (const c of n.children) collectLexicalText(c, out);
}

function collectPageText(pages: readonly Page[]): string[] {
    const out: string[] = [];
    for (const page of pages) {
        for (const el of page.elements) {
            if (el.type === 'text') {
                collectLexicalText(el.editorState?.root, out);
            } else if (el.type === 'legend') {
                out.push(el.title);
                for (const row of el.rows) out.push(row.abbrev, row.name);
            }
        }
    }
    return out;
}

/** Kana anywhere → `ja`; else any Han → `zh-TW`; else `en`. */
export function inferPatternLanguage(pages: readonly Page[]): PatternLanguage {
    const texts = collectPageText(pages);
    if (texts.some((t) => KANA.test(t))) return 'ja';
    if (texts.some((t) => HAN.test(t))) return 'zh-TW';
    return 'en';
}

/**
 * What the bundled Latin faces cover, by their latin and latin-ext subsets: Basic Latin through Latin Extended-B, IPA
 * and spacing/combining marks, Latin Extended Additional, general punctuation, currency, ℓ, ™, Latin Extended-C/-D.
 * Any other character falls through every Latin face to the stack's CJK part (design §5.4a: the CJK faces carry the
 * JIS / Big5 symbols ★ → ①).
 */
const LATIN_FACE_COVERAGE = /^[\u0000-\u036f\u1d00-\u1dbf\u1e00-\u1eff\u2000-\u206f\u20a0-\u20c0\u2113\u2122\u2c60-\u2c7f\ua720-\ua7ff\ufeff\ufffd]*$/;

/** True when some character of `text` is outside the Latin faces, so it renders in (and lays out by) a CJK face. */
export function needsCjkFace(text: string): boolean {
    return !LATIN_FACE_COVERAGE.test(text);
}

export const CJK_FONT_VAR = '--pattern-cjk-font';

/** CJK faces per language, primary first. */
export const CJK_FAMILIES: Readonly<Record<PatternLanguage, readonly [string, string]>> = {
    ja: ['Kosugi Maru', 'Huninn'],
    en: ['Kosugi Maru', 'Huninn'],
    'zh-TW': ['Huninn', 'Kosugi Maru'],
};

export function primaryCjkFamily(lang: PatternLanguage): string {
    return CJK_FAMILIES[lang][0];
}

export function cjkFontStack(lang: PatternLanguage): string {
    return CJK_FAMILIES[lang].map((f) => `'${f}'`).join(', ');
}

export function patternRootProps(lang: PatternLanguage): {
    'data-pattern-lang': PatternLanguage;
    style: Record<string, string>;
} {
    return { 'data-pattern-lang': lang, style: { [CJK_FONT_VAR]: cjkFontStack(lang) } };
}
